import { useCallback, useMemo } from 'react';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { ActionOutboxEntityType, Tag } from 'src/WebApiController';

export const useTagsFormBody = (
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent,
  tagsCurrentEntity: Tag[],
  onTagsUpdate: (tagsCurrentEntity: Tag[]) => void
) => {
  const { tagsMetadata: existingTagsCurrentType = [] } =
    useTagsForEntityType(entityType);

  const tagsWithEmptyValue = useMemo(() => {
    // Fill in the ordinal for tags that don't have it
    const hasEveryOrdinal = tagsCurrentEntity.every((t) => t.ordinal != null);

    let tagsOrdinalFilled: Tag[] = [...tagsCurrentEntity];
    if (!hasEveryOrdinal) {
      tagsOrdinalFilled = tagsOrdinalFilled
        .toSorted((t1, t2) => t1.key.localeCompare(t2.key))
        .map((t, i) => ({ ...t, ordinal: i }));
    }

    tagsOrdinalFilled = tagsOrdinalFilled.toSorted(
      (t1, t2) => (t1.ordinal ?? 0) - (t2.ordinal ?? 0)
    );

    // Add tags that don't have value
    (existingTagsCurrentType ?? []).forEach((t) => {
      if (!tagsOrdinalFilled.find((t2) => t2.key === t.key)) {
        tagsOrdinalFilled.push({
          ...t,
          value: '',
          ordinal: tagsOrdinalFilled.length,
        });
      }
    });

    return tagsOrdinalFilled;
  }, [existingTagsCurrentType, tagsCurrentEntity]);

  const tagsWithValue = useMemo(() => {
    return tagsWithEmptyValue.filter((t) => t.value);
  }, [tagsWithEmptyValue]);

  const onTagsUpdateHandler = useCallback(
    (newTags: Tag[]) => {
      // Filter out tags with empty value
      const tagsWithValue = newTags.filter((t) => t.value);
      // De-duplicate tags
      let tagsDeDup: Tag[] = [];
      tagsWithValue.forEach((t) => {
        if (
          !tagsDeDup.find(
            (t2) =>
              t2.key === t.key &&
              t2.value === t.value &&
              t2.sourceEntityId === t.sourceEntityId
          )
        ) {
          tagsDeDup.push(t);
        }
      });
      tagsDeDup = tagsDeDup.map((t, i) => ({ ...t, ordinal: i }));
      onTagsUpdate(tagsDeDup);
    },
    [onTagsUpdate]
  );

  const onTagValueChange = useCallback(
    (tag: Tag, newValue: string) => {
      const newTags = tagsWithEmptyValue.map((t) => {
        if (
          t.key === tag.key &&
          t.value === tag.value &&
          t.sourceEntityId === tag.sourceEntityId
        ) {
          return { ...t, value: newValue };
        }
        return t;
      });
      onTagsUpdateHandler(newTags);
    },
    [onTagsUpdateHandler, tagsWithEmptyValue]
  );

  return {
    tagsWithEmptyValue,
    tagsWithValue,
    onTagsUpdateHandler,
    onTagValueChange,
    existingTagsCurrentType,
  };
};
